import { Injectable, inject, signal } from '@angular/core';
import { last } from 'lodash-es';
import { LayoutEntry, SignalGroupProvider, SignalLayoutEntry } from './signal-group-provider';
import { RangeMap, SignalSettingsProvider, StyleMap } from './signal-settings-provider';

export type SignalLayoutEntryWithoutSlot = Omit<SignalLayoutEntry, 'slot'>;

export type LayoutEntryWithoutSlot = SignalLayoutEntryWithoutSlot | { type: 'divider'; key: string };

export interface StoredChartConfiguration {
    [key: string]: {
        signalStyles: StyleMap;
        signalRanges: RangeMap;
        layoutEntriesWithoutSlot: LayoutEntryWithoutSlot[];
    };
}

@Injectable({
    providedIn: 'root',
})
export class ChartConfigurationProvider {
    readonly chartConfigurationNames = signal<string[]>([]);

    private storedChartConfigurations: StoredChartConfiguration = {};
    private readonly signalGroupProvider = inject(SignalGroupProvider);
    private readonly signalSettingsProvider = inject(SignalSettingsProvider);

    constructor() {
        this.loadFromStorage();
    }

    addConfiguration(key: string, config: any) {
        this.storedChartConfigurations[key] = config;
        this.writeToStorage();
    }

    saveConfiguration(fileNameToSave: string) {
        if (!fileNameToSave) return;

        const layoutEntries: LayoutEntry[] = this.signalGroupProvider.layout;
        const layoutEntriesWithoutSlot: LayoutEntryWithoutSlot[] = layoutEntries.map((entry) => {
            if (entry.type === 'signal') {
                const { slot, ...rest } = entry as SignalLayoutEntry;
                return rest as SignalLayoutEntryWithoutSlot;
            }
            return entry as { type: 'divider'; key: string };
        });

        const signalRanges = this.signalSettingsProvider.ranges;
        const signalStyles = this.signalSettingsProvider.computedStyles;
        const newConfig = {
            layoutEntriesWithoutSlot,
            signalRanges,
            signalStyles,
        };

        this.storedChartConfigurations[fileNameToSave] = newConfig;
        this.writeToStorage();
    }

    loadConfiguration(key: string) {
        this.signalGroupProvider.setAppliedConfigurationFileName(key);
        const selectedConfig = Object.assign(this.storedChartConfigurations[key]);
        const layoutEntries: LayoutEntry[] = [...this.signalGroupProvider.layout];
        const newLayoutEntries: LayoutEntry[] = [];
        const newLayoutEntriesWithNoSlot: LayoutEntry[] = [];

        selectedConfig.layoutEntriesWithoutSlot.forEach((layoutEntryWithoutSlot: any) => {
            if (layoutEntryWithoutSlot.type === 'signal') {
                const signalInLogDataIndex = layoutEntries.findIndex((e) => e.key === layoutEntryWithoutSlot.key);
                if (signalInLogDataIndex > -1) {
                    newLayoutEntries.push(layoutEntries[signalInLogDataIndex]);
                    layoutEntries.splice(signalInLogDataIndex, 1);
                } else {
                    newLayoutEntriesWithNoSlot.push({
                        ...layoutEntryWithoutSlot,
                        slot: null,
                        data: [[0, 0]],
                    });
                }
            } else {
                newLayoutEntries.push(layoutEntryWithoutSlot as LayoutEntry);
                newLayoutEntriesWithNoSlot.push(layoutEntryWithoutSlot as LayoutEntry);
            }
        });

        const dividersOnly = newLayoutEntries.filter((entry) => entry.type === 'divider');
        let lastDividerIndex = -1;
        if (dividersOnly.length) {
            lastDividerIndex = parseInt(dividersOnly[dividersOnly.length - 1].key.slice(-1), 10);
            if (isNaN(lastDividerIndex)) lastDividerIndex = dividersOnly.length;
        }

        if (layoutEntries.length) {
            if (last(newLayoutEntries)?.type !== 'divider') {
                lastDividerIndex += 1;
                newLayoutEntries.push({
                    key: 'divider' + lastDividerIndex,
                    type: 'divider',
                });
            }
            let lastEntryIsDivider = false;
            layoutEntries.forEach((entry) => {
                if (entry.type === 'signal') {
                    newLayoutEntries.push(entry);
                    lastEntryIsDivider = false;
                } else {
                    if (!lastEntryIsDivider) {
                        lastDividerIndex += 1;
                        newLayoutEntries.push({
                            key: 'divider' + lastDividerIndex,
                            type: 'divider',
                        });
                        lastEntryIsDivider = true;
                    }
                }
            });
        }

        this.removeLastEntryIfDivider(newLayoutEntries);
        this.removeLastEntryIfDivider(newLayoutEntriesWithNoSlot);

        this.applyChartConfiguration(newLayoutEntries, newLayoutEntriesWithNoSlot, selectedConfig);
    }

    deleteConfiguration(key: string) {
        delete this.storedChartConfigurations[key];
        this.writeToStorage();
    }

    renameConfiguration(oldKey: string, newKey: string) {
        if (!newKey || this.storedChartConfigurations[newKey]) return; // Avoid overwriting existing
        this.storedChartConfigurations[newKey] = this.storedChartConfigurations[oldKey];
        delete this.storedChartConfigurations[oldKey];
        this.writeToStorage();
    }

    getConfiguration(key: string): any {
        return this.storedChartConfigurations[key];
    }

    getAllConfigurations(): StoredChartConfiguration {
        return this.storedChartConfigurations;
    }

    private loadFromStorage() {
        const chartConfigs = window.localStorage.getItem('chartConfigs');
        if (chartConfigs) {
            try {
                const value = JSON.parse(chartConfigs);
                if (typeof value === 'object' && value !== null) {
                    this.storedChartConfigurations = value;
                    this.chartConfigurationNames.set(Object.keys(value));
                }
            } catch (e) {
                console.error('Failed to parse chart configs from local storage', e);
            }
        }
    }

    private writeToStorage() {
        const newConfigStr = JSON.stringify(this.storedChartConfigurations);
        window.localStorage.setItem('chartConfigs', newConfigStr);
        this.chartConfigurationNames.set(Object.keys(this.storedChartConfigurations));
    }

    private removeLastEntryIfDivider(entries: LayoutEntry[]) {
        do {
            if (last(entries)?.type === 'divider') {
                entries.pop();
            } else {
                break;
            }
        } while (entries.length > 0);
    }

    private applyChartConfiguration(
        newLayoutEntries: LayoutEntry[],
        newLayoutEntriesWithNoSlot: LayoutEntry[],
        selectedConfig: {
            signalStyles: StyleMap;
            signalRanges: RangeMap;
            layoutEntriesWithoutSlot: LayoutEntryWithoutSlot[];
        },
    ) {
        this.signalGroupProvider.layout = newLayoutEntries;
        this.signalGroupProvider.layoutNoSlot = newLayoutEntriesWithNoSlot;

        const signalRangesKeys = Object.keys(selectedConfig.signalRanges);
        for (const key of signalRangesKeys) {
            this.signalSettingsProvider.setRange(key, selectedConfig.signalRanges[key]);
        }

        const signalStylesKeys = Object.keys(selectedConfig.signalStyles);
        for (const key of signalStylesKeys) {
            this.signalSettingsProvider.setStyle(key, selectedConfig.signalStyles[key]);
        }
    }
}
